import { Component, OnInit, EventEmitter, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UploadOutput, UploadInput, UploadFile, UploaderOptions, UploadStatus } from 'ngx-uploader-esb';
import { filter, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';

import { PlaylistItemDialogService } from '../../../core/services/dialog-services/playlist-item-dialog.service';
import { ItemTabComponent } from '../../_basic/tab-playlist-item.component';
import { CustomFileModel } from '../../../core/models/entities/file.type';
import { AppConstants } from '../../../app.constants';
import { PlaylistItemType } from '../../../core/models/enums/playlist-item-type.enum';
import { FileUploadingEnum } from '../../../core/enums/file-uploading.enum';
import { PlaylistItemDTO } from '../../../core/models/view-models/playlist-item-models/playlist-item-dto.model';
import { FileComponent } from './file/file.component';
import { ToastrMessageEnum } from '../../../core/enums/toastr-messages/toastr-message.enum';
import { PlaylistItemChangeSourceModel } from '../../../core/models/view-models/playlist-item-models/playlist-item-change-source-model';
import { PlaylistItemLinkModel } from '../../../core/models/view-models/playlist-item-models/playlist-item-link.model';
import { DeleteDialogService } from '../../../core/services/dialog-services/delete-dialog.service';

@Component({
  selector: 'tab-file',
  templateUrl: './tab-file.component.html',
  styleUrls: [
    './tab-file.component.scss',
    '../../modal-playlist-item/playlist-item-dialog.component.scss'
  ]
})
export class TabFileComponent extends ItemTabComponent implements OnInit, OnDestroy {

  public ShowReadyFile = false;
  public isDragOver = false;
  public percentage: number;
  public isItemConverting: boolean;
  public appConts = AppConstants;

  // ngx file uploader options
  public options: UploaderOptions;
  public files: UploadFile[];
  public uploadInput: EventEmitter<UploadInput>;
  private uploadedFile: UploadFile;

  constructor(
    private readonly playlistItemDialogService: PlaylistItemDialogService,
    private readonly toastrService: ToastrService,
    private readonly confirmDialog: DeleteDialogService
  ) {
    super();
    this.files = []; // local uploading files array
    this.uploadInput = new EventEmitter<UploadInput>(); // input events, we use this to emit data to ngx-uploader

    this.filesService.uploadInput$
      .pipe(takeUntilDestroyed())
      .subscribe((uploadData) => {
        this.uploadInput.emit(uploadData)
      });
  }

  ngOnInit(): void {
    this.ShowReadyFile =
      this.playlistItems?.length &&
      this.playlistItems[0].PlaylistItemType === PlaylistItemType.RecentFile;

    if (this.ShowReadyFile) {
      this.playlistItems.forEach(item => {
        item.percentage = item.PlaylistItemLinks[0]?.FileId === null
          ? AppConstants.ZERO_DOWNLOADED_FILE
          : AppConstants.FULLY_DOWNLOADED_FILE;
      });
    }
    else {
      this.playlistItems = [];
    }

    if (this.playlistItemDialogService.allowClose) {
      this.onUploadOutput({ type: 'removedAll' });
    }

    if (this.ShowReadyFile) {
      this.playlistItems.forEach(item => {
        if (!item.PlaylistItemLinks[0].content) {
          this.filesService.previewContent(item);
        }
      });
    }
  }

  submit() {
    const fileComponentData: FileComponent[] = this.filesComponents.filter((item: FileComponent) => {
      return !!item.playlistItem;
    });

    this.playlistItems.forEach((playlistItem, index) => {
      playlistItem.PlaylistItemTitle = playlistItem.PlaylistItemLinks.length > 1 ?
        fileComponentData[index].playlistItemName || AppConstants.MULTIPLE_ITEMS_TITLE :
        playlistItem.PlaylistFileName;

      playlistItem.IsLocal = false;
      playlistItem.Launcher = fileComponentData[index].selectedLauncher;
      playlistItem.PlaylistItemType = PlaylistItemType.RecentFile;
    });

    super.saveChanges(this.playlistItems);
    this.ShowReadyFile = false;
  }

  onUploadOutput(output: UploadOutput, isToAddToPlaylistItems: boolean = true): void {
    this.isDragOver = false;

    const index = this.files.findIndex(
      file =>
        typeof output.file !== 'undefined' && file.id === output.file.id
    );

    switch (output.type) {
      case FileUploadingEnum.AllAddedToQueue:
        // when all files added in queue
        // uncomment this if you want to auto upload files when added
        this.filesService.startUpload(this.files);
        break;

      case FileUploadingEnum.AddedToQueue:
        if (typeof output.file !== 'undefined') {
          // add file to array when added
          if (!isToAddToPlaylistItems) {
            this.files = [];
          }

          this.files.push(output.file);
          this.uploadedFile = output.file;

          if (isToAddToPlaylistItems) {
            const addedItem = new PlaylistItemDTO();
            addedItem.PlaylistItemTitle = this.uploadedFile.name;
            addedItem.PlaylistFileName = this.uploadedFile.name;
            this.playlistItems.push(addedItem);
          }

          this.ShowReadyFile = true;
        }

        break;

      case FileUploadingEnum.Uploading:
        if (typeof output.file !== 'undefined') {
          // update current data in files array for uploading file
          this.files[index] = output.file;

          if (isToAddToPlaylistItems) {
            this.playlistItems[index].percentage = output.file.progress.data.percentage;
          }
          this.percentage = output.file.progress.data.percentage;
          this.changeRef.detectChanges();
        }
        break;

      case FileUploadingEnum.Removed:
        // remove file from array when removed
        this.files = this.files.filter(
          (file: UploadFile) => file !== output.file
        );
        this.percentage = AppConstants.FULLY_DOWNLOADED_FILE;
        this.uploadInput.emit({ type: 'cancel', id: output.file?.id });
        break;

      case FileUploadingEnum.RemovedAll:
        this.uploadInput.emit({ type: 'removeAll' });
        this.uploadInput.emit({ type: 'cancelAll' });
        this.uploadedFile = null;
        this.playlistItemDialogService.uploadStatus = UploadStatus.Cancelled;
        break;

      case FileUploadingEnum.Done:
        if (output.file.responseStatus === AppConstants.SUCCSESS_REQUEST_RESPONSE_STATUS) {
          const responseFile: CustomFileModel = output.file.response as CustomFileModel;

          if (isToAddToPlaylistItems) {
            this.playlistItems[index].PlaylistItemLinks.push(new PlaylistItemLinkModel());
            this.playlistItems[index].PlaylistItemLinks[0].FileId = responseFile.Id;
            this.playlistItems[index].PlaylistItemLinks[0].File = responseFile;
            this.playlistItems[index].PlaylistItemType = PlaylistItemType.RecentFile;

            if (responseFile.Duration) {
              this.playlistItems[index].ExactVideoDuration = responseFile.Duration;
              this.playlistItems[index].Duration = responseFile.Duration;
              this.playlistItems[index].PlaylistItemLinks[0].duration = responseFile.Duration;
              this.playlistItems[index].useExactDuration = true;
            }

            if (AppConstants.IMAGE_FORMAT_REGULAR.test(output.file.type)) {
              const reader = new FileReader();
              reader.readAsDataURL(output.file.nativeFile);
              reader.onload = () => {
                this.playlistItems[index].PlaylistItemLinks[0].content = reader.result;
              }
            } else if (AppConstants.VIDEO_FORMAT_REGULAR.test(output.file.type)) {
              this.filesService.previewContent(this.playlistItems[index]);
            }

            this.filesService.setIcon(this.playlistItems[index]);

            if (this.playlist.IsAndroid && this.filesService.getFileType(this.playlistItems[index]).includes(AppConstants.PPT_FILE_TYPE)) {
              this.convertPPTFile(this.playlistItems[index], false);
            }

            if (!this.playlist.IsAndroid) {
              this.setDefaultLauncher(this.playlistItems[index], index);
            }

            // if (this.playlist.IsAndroid && this.filesService.shouldConvertPortraitToLandscape(this.playlistItems[index])) {
            //   this.convertMP4ToLandscape(this.playlistItems[index]);
            // }
          }
        }
        else {
          this.toastrService.error(output.file.response.Message, `${ToastrMessageEnum.FailedToUploadTitle}: ${output.file.name}`);

          this.playlistItems.splice(index, 1);
          this.files.splice(index, 1);

          if (this.playlistItems.length === 0) {
            this.ShowReadyFile = false;
            this.files = [];
            this.onUploadOutput({ type: 'removedAll' });
          }
        }
        break;
    }

    if (this.uploadedFile) {
      this.playlistItemDialogService.uploadStatus = this.uploadedFile.progress.status;
    }
  }

  onRemoveItem(index: number): void {
    this.confirmDialog.show().pipe(
      filter(isToContinue => isToContinue),
      tap(() => {
        this.removeItem(index);

        if (!this.files.length && !this.playlistItems.length) {
          this.ShowReadyFile = false;
        }

        if (!this.files.length) return;

        if (this.playlistItems.length) {
          this.onUploadOutput({ type: 'removed', file: this.files[index] });
        }
        else {
          this.ShowReadyFile = false;
          this.files = [];
          this.onUploadOutput({ type: 'removedAll' });
        }
      })
    ).subscribe();
  }

  onChangeSourceField(data: PlaylistItemChangeSourceModel): void {
    this.onChangeField(data.item, data.sourceId);
  }

  onChangeSourceData(data: PlaylistItemChangeSourceModel): void {
    this.onSourceChanged(data.event, data.sourceId, data.item);
  }

  onChangeMode(data: PlaylistItemChangeSourceModel): void {
    this.onChangeMCLMode(data.item, data.mclMode);
  }

  onDetermineDuration(item: PlaylistItemDTO): void {
    this.calculateAndSetDuration(item);
  }

  onInitDurations(item: PlaylistItemDTO): void {
    this.initDurationsList(item);
  }

  trackByIndex(index: number) {
    return index;
  }

  ngOnDestroy(): void {
    this.onUploadOutput({ type: 'removedAll' });
    super.ngOnDestroy();
  }

  private setDefaultLauncher(item: PlaylistItemDTO, index: number) {
    this.setLauncherBasedOnFileType(item);
    this.selectedLaunchers[index] = item.Launcher;
  }
}
